// The house's rooms: what each one is called, and where it sits on the plan.
//
// Nothing here is drawn as text — the names existed as on-map labels while
// the rooms were being mapped and were taken off once that was done. What the
// map uses now is the GEOMETRY: each room's anchor picks out its polygon in
// the floor-plan drawing, which is the shape that lights up when the room's
// lights are on.
//
// Coordinates are the CubiCasa plan space shared with the sensor definitions
// in SecurityWidget.tsx — the anchor is the label's CENTER, placed in open
// floor near the middle of the room. They were read off gridded renders of
// each floor (scratchpad floor-flat.html) and are nudged by hand; HMR makes
// that a live edit on the kiosk.
//
// Upper floor names are Dakota's own (2026-09-25). Main and basement names
// are inferred from fixtures and the sensor placements and still want a pass.

import type { LightFloorId } from './lights';

export interface RoomLabel {
  label: string;
  floor: LightFloorId;
  x: number;
  y: number;
  // Key into LIGHT_ROOMS. When that room has a light on, its polygon on the
  // map fills yellow and takes a glowing border.
  lightKey?: string;
  // Extra anchors for rooms the drawing splits across several polygons — a
  // bathroom with its toilet in a separate closet is one room to a person.
  // Each point resolves to its own polygon and they highlight together.
  alsoAt?: Array<[number, number]>;
  // Straight cuts through the room, as a line [ax, ay, bx, by] in plan
  // coords: everything to the RIGHT of travelling a->b is trimmed away, and
  // the room keeps a clean straight edge along the line. The drawing's
  // polygons sometimes run past a wall into dead space, which is what these
  // are for. Swap the two ends to keep the other side.
  cuts?: Array<[number, number, number, number]>;
  // Glow footprint in plan units, roughly the room's half-extents.
  rx?: number;
  ry?: number;
}

export const ROOM_LABELS: RoomLabel[] = [
  // ---- Upper ----
  { label: 'Master Bedroom', floor: 'upper', x: 700, y: 350, lightKey: 'master', rx: 280, ry: 210 },
  // Second anchor: the toilet closet off the far corner, drawn as its own
  // polygon but part of the same bathroom.
  {
    label: 'Master Bath', floor: 'upper', x: 1270, y: 250,
    alsoAt: [[1345, 499]], lightKey: 'master_bath',
  },
  { label: 'Master Closet', floor: 'upper', x: 1120, y: 449, lightKey: 'master_closet' },
  // The wedge by the bedroom doors plus the landing running over to the top
  // of the stairs — two polygons, one hallway.
  // The landing polygon also carries the staircase, which runs as an L: one
  // flight across the top (y < 668) and a second dropping down the side
  // (x > 1219). Two cuts take both off and leave the hall its floor.
  {
    label: 'Hallway', floor: 'upper', x: 735, y: 850,
    alsoAt: [[989, 611]],
    lightKey: 'upstairs_hall',
    // Both stair flights sit east of x=1043, so one vertical cut there drops
    // the whole staircase and keeps the corridor that runs north past the
    // bathroom. A horizontal cut can't help: the hall is L-shaped and
    // intersecting half-planes only ever makes convex shapes.
    cuts: [[1043, 0, 1043, 2000]],
  },
  { label: 'Upstairs Bath', floor: 'upper', x: 600, y: 630, lightKey: 'upstairs_bath' },
  { label: 'Office', floor: 'upper', x: 365, y: 960, lightKey: 'office', rx: 200, ry: 160 },
  {
    label: 'Upstairs Guest Room', floor: 'upper', x: 595, y: 1190,
    lightKey: 'guest_room_2', rx: 190, ry: 150,
  },
  { label: 'Small Room', floor: 'upper', x: 930, y: 905, lightKey: 'small_room', rx: 160, ry: 130 },
  // Not a room — the void over the main-floor living room. Named so the big
  // empty rectangle reads as intentional.
  { label: 'Open to Below', floor: 'upper', x: 1400, y: 950 },

  // ---- Main ----
  { label: 'Dining Room', floor: 'main', x: 700, y: 330, lightKey: 'dining', rx: 280, ry: 200 },
  { label: 'Kitchen', floor: 'main', x: 1250, y: 330, lightKey: 'kitchen', rx: 200, ry: 190 },
  { label: 'Pantry', floor: 'main', x: 1609, y: 404, lightKey: 'pantry' },
  { label: 'Living Room', floor: 'main', x: 1400, y: 950 },
  // The stairs are drawn as part of the entry's polygon and simply highlight
  // with it; the second anchor carries the strip through to the dining room.
  {
    label: 'Entry', floor: 'main', x: 1200, y: 990,
    alsoAt: [[940, 600]], lightKey: 'entry_way',
  },
  { label: 'Laundry', floor: 'main', x: 680, y: 645, lightKey: 'laundry' },
  { label: 'Garage', floor: 'main', x: 620, y: 1300 },
  // The drawing's polygon carries on past the wall behind the toilet and
  // tapers to a dead wedge in the corner. Cut it square across the room,
  // perpendicular to the room's long axis, just behind the toilet.
  {
    label: 'Powder Room', floor: 'main', x: 960, y: 950,
    lightKey: 'powder_room', rx: 120, ry: 110,
    cuts: [[1144, 996, 980, 1112]],
  },

  // ---- Basement ----
  { label: 'Basement', floor: 'basement', x: 900, y: 440, lightKey: 'basement', rx: 350, ry: 250 },
  {
    label: 'Basement Guest Room', floor: 'basement', x: 1570, y: 500,
    lightKey: 'basement_guest',
  },
  // Hall and staircase share one polygon in the drawing, so they split along
  // the same line: the hall takes everything below it, the stairs everything
  // above — which hands the stairs the landing beside them as well.
  {
    label: 'Hallway', floor: 'basement', x: 1100, y: 730,
    cuts: [[0, 668, 2000, 668]], lightKey: 'basement_hall',
  },
  {
    label: 'Stairs', floor: 'basement', x: 1090, y: 600,
    alsoAt: [[1282, 606]],
    cuts: [[2000, 668, 0, 668]],
    lightKey: 'basement_stairs',
  },
  // Anchor sits on open floor, clear of the vanity, toilet and shower — the
  // matcher takes the smallest polygon containing the point, and a fixture
  // counts as one, so an anchor dropped on the sink highlights just the sink.
  { label: 'Basement Bath', floor: 'basement', x: 1105, y: 800 },
  { label: 'Storage', floor: 'basement', x: 1400, y: 920, lightKey: 'storage' },
];

// Highlight a room's boundary WITHOUT touching a real light — for checking
// that a highlight lands on the room you meant. Set it to a room's name as it
// appears above ("Garage", "Living Room", …), or 'all' for every room at once.
// Leave as '' when not testing; a URL ?lit= overrides it, and it is ignored
// entirely in a production build.
//
// This is here rather than in the URL so a room can be named in conversation
// and swapped in on the wall by HMR, one at a time.
export const PREVIEW_ROOM = '';
